import { TokenInfo } from "@labb/constellation-core-types";
import { API_ERROR_CODES } from "../../onboarding-api";

export enum PegaApplicationStatus {
  Empty = "Empty",
  "Open-InProgress" = "Open-InProgress",
  "Resolved-Completed" = "Resolved-Completed",
}

export type PegaState = {
  applicationAssignmentKey: string;
  applicationStatus: PegaApplicationStatus;
};

export type PegaApplication = {
  applicationStatus: PegaApplicationStatus;
  caseID: string;
  applicationAssignmentKey: string;
  productName: string;
  productCode: string;
  productDescription: string;
  updateDateTime: string;
  tenantTemplateCode: string;
  onboardingRegion: string;
  country: string;
  customerID: string;
  customerType: string;
  sourcePlatform: string;
};

type PegaApplicationsListResponse = {
  applications: PegaApplication[];
};

export type PegaStateResponse = PegaState | PegaApplicationsListResponse;

function isApplicationsListResponse(
  data: PegaStateResponse,
): data is PegaApplicationsListResponse {
  return Array.isArray((data as PegaApplicationsListResponse).applications);
}

export function resolvePegaStateResponse(
  data: PegaStateResponse,
):
  | { kind: "single"; state: PegaState }
  | { kind: "list"; applications: PegaApplication[] } {
  if (isApplicationsListResponse(data)) {
    return { kind: "list", applications: data.applications };
  }
  return { kind: "single", state: data };
}

type ApiGetPegaStateResult = {
  data?: PegaStateResponse;
  errorCode?: API_ERROR_CODES;
};

export async function apiGetPegaState(
  baseUrl: string,
  pegaToken: TokenInfo,
  startingFields?: {
    Product?: string | null;
    AccountID?: string | null;
    MultiAsset?: boolean | null;
    TenantID?: string | null;
    Context?: string | null;
    OnboardingRegion?: string | null;
    Domain?: string | null;
    Platform?: string | null;
    CustomerType?: string | null;
    IsFromPlatform?: boolean | null;
    Channel?: string | null;
  },
): Promise<ApiGetPegaStateResult> {
  const queryParams = buildQueryParams(startingFields);
  const url = `${baseUrl}/api/nexus/v1/onboarding/application?${queryParams}`;

  return fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: `Bearer ${pegaToken.access_token}`,
    },
  })
    .then(async (response) => {
      if (!response.ok) {
        if (response.status === 401) {
          return {
            errorCode: API_ERROR_CODES.PEGA_TOKEN_EXPIRED,
          };
        }

        return {
          errorCode: API_ERROR_CODES.GENERIC,
        };
      }

      const responseJson = await response.json();

      return { data: responseJson };
    })
    .catch(() => {
      return {
        errorCode: API_ERROR_CODES.GENERIC,
      };
    });
}

function buildQueryParams(
  fields?: {
    Product?: string | null;
    AccountID?: string | null;
    MultiAsset?: boolean | null;
    TenantID?: string | null;
    Context?: string | null;
    OnboardingRegion?: string | null;
    Domain?: string | null;
    Platform?: string | null;
    CustomerType?: string | null;
    IsFromPlatform?: boolean | null;
    Channel?: string | null;
    CaseID?: string | null;
  },
): string {
  const params = new URLSearchParams();

  if (fields?.MultiAsset) {
    params.append('MultiAsset', fields.MultiAsset.toString());
  }
  if (fields?.Product) {
    params.append('Product', fields.Product);
  }
  if (fields?.AccountID) {
    params.append('AccountId', fields.AccountID.toString());
  }
  if (fields?.TenantID) {
    params.append('TenantID', fields.TenantID);
  }
  if (fields?.Context) {
    params.append('Context', fields.Context);
  }
  if (fields?.OnboardingRegion) {
    params.append('OnboardingRegion', fields.OnboardingRegion);
  }
  if (fields?.Domain) {
    params.append('Domain', fields.Domain);
  }
  if (fields?.Platform) {
    params.append('Platform', fields.Platform);
  }
  if (fields?.CustomerType) {
    params.append('CustomerType', fields.CustomerType);
  }
  if (fields?.IsFromPlatform) {
    params.append('IsFromPlatform', fields.IsFromPlatform.toString());
  }
  if (fields?.Channel) {
    params.append('Channel', fields.Channel);
  }
  if (fields?.CaseID) {
    params.append("CaseID", fields.CaseID);
  }

  return params.toString();
}
